import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Camera, Lock, Mail, Phone, MapPin, Calendar, Shield, Bell, Eye, EyeOff, Check, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

// Predefined avatars
const AVATAR_OPTIONS = [
  { id: 'avatar1', url: '/avatars/avatar1.png', color: '#0C4432' },
  { id: 'avatar2', url: '/avatars/avatar2.png', color: '#49C5B6' },
  { id: 'avatar3', url: '/avatars/avatar3.png', color: '#DF6C4F' },
  { id: 'avatar4', url: '/avatars/avatar4.png', color: '#F59E0B' },
  { id: 'avatar5', url: '/avatars/avatar5.png', color: '#8B5CF6' },
  { id: 'avatar6', url: '/avatars/avatar6.png', color: '#EC4899' },
];

interface UserProfile {
  name: string;
  email: string;
  phone: string;
  address: string;
  profilePicture: string | null;
  avatarColor: string;
  dateJoined: string;
}

export default function RenterProfile() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile state
  const [profile, setProfile] = useState<UserProfile>({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    profilePicture: null,
    avatarColor: '#0C4432',
    dateJoined: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Edit modes
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Close account state
  const [showCloseAccountModal, setShowCloseAccountModal] = useState(false);
  const [closeAccountConfirm, setCloseAccountConfirm] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get<any>('/users/me');
      setProfile({
        name: response.name || user?.name || '',
        email: response.email || user?.email || '',
        phone: response.phone || '',
        address: response.address || '',
        profilePicture: response.profilePicture || null,
        avatarColor: response.avatarColor || '#0C4432',
        dateJoined: response.createdAt || new Date().toISOString(),
      });
    } catch (err) {
      // Use auth context data as fallback
      setProfile(prev => ({
        ...prev,
        name: user?.name || '',
        email: user?.email || '',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put('/users/me', {
        name: profile.name,
        phone: profile.phone,
        address: profile.address,
        avatarColor: profile.avatarColor,
      });
      setSuccess('Profile updated successfully');
      setEditingProfile(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelect = async (avatarId: string, color: string) => {
    setProfile(prev => ({
      ...prev,
      profilePicture: avatarId,
      avatarColor: color,
    }));
    setShowAvatarPicker(false);

    // Save immediately
    try {
      await api.put('/users/me', {
        profilePicture: avatarId,
        avatarColor: color,
      });
      // Refresh user in auth context so UserMenu updates
      await refreshUser();
      setSuccess('Avatar updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update avatar');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    // Convert to base64 for preview (in production, upload to S3)
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      setProfile(prev => ({ ...prev, profilePicture: base64 }));
      setShowAvatarPicker(false);

      // In production, upload to S3 and save URL
      try {
        await api.put('/users/me', { profilePicture: base64 });
        // Refresh user in auth context so UserMenu updates
        await refreshUser();
        setSuccess('Profile picture updated');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to upload picture');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (passwordForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setSuccess('Password changed successfully');
      setEditingPassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password. Please check your current password.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseAccount = async () => {
    if (closeAccountConfirm !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    setSaving(true);
    try {
      await api.delete('/users/me');
      logout();
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to close account');
      setSaving(false);
    }
  };

  const getUserInitials = () => {
    if (!profile.name) return '?';
    const names = profile.name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <PageHeader
        title="My Profile"
        subtitle="Manage your personal information and settings"
        icon={<User className="w-8 h-8" />}
        backPath="/dashboard"
      />

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{success}</span>
        </div>
      )}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <X className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* Profile Picture Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Picture</h2>
          <div className="flex items-center gap-6">
            {/* Current Avatar */}
            <div className="relative">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden"
                style={{ backgroundColor: profile.avatarColor }}
              >
                {profile.profilePicture?.startsWith('data:') ? (
                  <img src={profile.profilePicture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span>{getUserInitials()}</span>
                )}
              </div>
              <button
                onClick={() => setShowAvatarPicker(true)}
                className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>

            <div>
              <p className="text-gray-600 mb-2">Choose an avatar or upload your own photo</p>
              <button
                onClick={() => setShowAvatarPicker(true)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Change Avatar
              </button>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
            {!editingProfile ? (
              <button
                onClick={() => setEditingProfile(true)}
                className="text-secondary hover:text-primary font-medium text-sm"
              >
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingProfile(false)}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="px-3 py-1 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              {editingProfile ? (
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900 py-2">{profile.name || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="flex items-center gap-2 py-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{profile.email}</p>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Verified</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              {editingProfile ? (
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              ) : (
                <div className="flex items-center gap-2 py-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-900">{profile.phone || 'Not set'}</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              {editingProfile ? (
                <input
                  type="text"
                  value={profile.address}
                  onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                  placeholder="Your address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              ) : (
                <div className="flex items-center gap-2 py-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-900">{profile.address || 'Not set'}</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
              <div className="flex items-center gap-2 py-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{formatDate(profile.dateJoined)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Link */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-primary" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">App Settings</h2>
                <p className="text-sm text-gray-500">Notifications, appearance, privacy, and more</p>
              </div>
            </div>
            <a
              href="/renter/settings"
              className="px-4 py-2 text-secondary hover:text-primary font-medium transition-colors"
            >
              Open Settings →
            </a>
          </div>
        </div>

        {/* Security Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Security</h2>
          </div>

          {!editingPassword ? (
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">Password</p>
                  <p className="text-sm text-gray-500">Last changed: Unknown</p>
                </div>
              </div>
              <button
                onClick={() => setEditingPassword(true)}
                className="text-secondary hover:text-primary font-medium text-sm"
              >
                Change Password
              </button>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditingPassword(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-lg shadow p-6 border-2 border-red-100">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-semibold text-red-600">Danger Zone</h2>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Close Account</p>
              <p className="text-sm text-gray-500">Permanently delete your account and all data</p>
            </div>
            <button
              onClick={() => setShowCloseAccountModal(true)}
              className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              Close Account
            </button>
          </div>
        </div>
      </div>

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Choose Avatar</h2>
                <button onClick={() => setShowAvatarPicker(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Color Avatars */}
              <p className="text-sm font-medium text-gray-700 mb-3">Select a color</p>
              <div className="grid grid-cols-6 gap-3 mb-6">
                {AVATAR_OPTIONS.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => handleAvatarSelect(avatar.id, avatar.color)}
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg hover:ring-2 hover:ring-offset-2 hover:ring-gray-300 transition-all"
                    style={{ backgroundColor: avatar.color }}
                  >
                    {getUserInitials()}
                  </button>
                ))}
              </div>

              {/* Upload Option */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Or upload a photo</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-secondary hover:text-secondary transition-colors flex items-center justify-center gap-2"
                >
                  <Camera className="w-5 h-5" />
                  Upload Photo
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">Max 5MB, JPG or PNG</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Account Modal */}
      {showCloseAccountModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Close Account</h2>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                This action is <strong>permanent</strong> and cannot be undone. All your data, including:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
                <li>Payment history</li>
                <li>Documents</li>
                <li>Messages</li>
                <li>Maintenance requests</li>
              </ul>
              <p className="text-gray-600 mb-4">will be permanently deleted.</p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <strong>DELETE</strong> to confirm
                </label>
                <input
                  type="text"
                  value={closeAccountConfirm}
                  onChange={(e) => setCloseAccountConfirm(e.target.value)}
                  placeholder="DELETE"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCloseAccountModal(false);
                    setCloseAccountConfirm('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCloseAccount}
                  disabled={closeAccountConfirm !== 'DELETE' || saving}
                  className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Closing...' : 'Close Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



